import React from 'react'
import { InfoWrapper, InfoTitleRow, MarketIcon, ContentFooter } from './index'
import { Stack, Skeleton, Button, styled, Paper, Typography } from '@mui/material'
import { ImageIcon } from '@/icon'
import Text from '@/components/Text'
import { grayText, secondaryText } from '@/constant'
import { ApplicationInfo } from '@/types'
import { MarketSortType } from '../index'
import empty from '@/asset/img/empty.webp'

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  boxShadow: 'none',
  borderBottom: '1px dashed rgba(0, 0, 0, 0.12)',
  '&:last-of-type': {
    borderBottom: 'none',
  },
}))

export type AppActivityType = 'enter' | 'purchase'
export interface MarketplaceProps {
  data: ApplicationInfo[]
  loading: boolean
  sort: MarketSortType
  enterApp: (app: ApplicationInfo) => void
  purchaseApp: (app: ApplicationInfo) => void
  onSortChange: (sort: MarketSortType) => void
}

const Marketplace: React.FC<MarketplaceProps> = (props) => {
  const { data, loading, enterApp, purchaseApp } = props
  const onActive = (action: AppActivityType, app: ApplicationInfo) => {
    switch (action) {
      case 'enter': {
        enterApp(app)
        break
      }
      case 'purchase': {
        purchaseApp(app)
        break
      }
    }
  }
  // @ts-ignore
  const onActiveStateChange = (value) => {
    onActive(!!value.expired_at ? 'enter' : 'purchase', value)
  }

  return (
    <InfoWrapper
      sx={{
        padding: '30px 14px 14px 14px',
        marginTop: '30px',
        flexDirection: 'column',
        gap: '24px',
      }}
    >
      <InfoTitleRow sx={{ height: '10px' }}>
        <Text
          weight='bold'
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            fontSize: '14px',
          }}
        >
          <MarketIcon />
          更多产品
        </Text>
      </InfoTitleRow>
      {!loading ? (
        data?.length > 0 ? (
          <Stack
            spacing={0}
            direction='column'
            alignItems='start'
            sx={{ width: '100%', textAlign: 'start', boxShadow: 'none' }}
          >
            {data.map((value) => {
              return (
                <Item key={value.id}>
                  <ImageIcon
                    src={value?.logo_url}
                    sx={{
                      width: '36px',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      aspectRatio: '1',
                      backgroundColor: '#eee',
                    }}
                    id='product-icon'
                  />
                  <Typography
                    variant='body2'
                    sx={{
                      pl: '14px',
                      width: '240px',
                      textAlign: 'left',
                      color: '#333',
                    }}
                    noWrap
                  >
                    {value.name}
                    <br />
                    <Typography variant='caption' sx={{ textAlign: 'left', color: 'grey' }} noWrap>
                      {value.slogan}
                    </Typography>
                  </Typography>
                  <Text
                    weight='regular'
                    size='xs'
                    sx={{
                      flex: 1,
                      color: secondaryText,
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      fontSize: '13px',
                      pl: '14px',
                      width: '150px',
                      textAlign: 'left',
                    }}
                    id='product-desc'
                  >
                    {value?.description}
                  </Text>
                  <ContentFooter id='product-footer'>
                    {!loading ? (
                      <>
                        {value.open === 1 ? (
                          <Button
                            variant='text'
                            // @ts-ignore
                            sx={{
                              minWidth: '88px',
                              width: '88px',
                              //准备下架 雷池web应用防火墙
                              display: value.url_prefix.includes('waf-cloud') && !value.expired_at && 'none',
                              fontWeight: 'light',
                              fontFamily: 'PingFangSC-Regular',
                            }}
                            onClick={() => onActiveStateChange(value)}
                          >
                            开始使用
                          </Button>
                        ) : value.landing_raw_url || value.landing_url ? (
                          <Button
                            variant='text'
                            // @ts-ignore
                            sx={{
                              minWidth: '88px',
                              width: '88px',
                              //准备下架 雷池web应用防火墙
                              display: value.url_prefix.includes('waf-cloud') && !value.expired_at && 'none',
                              fontWeight: 'light',
                              fontFamily: 'PingFangSC-Regular',
                            }}
                            onClick={() => {
                              if (value.landing_url) {
                                window.open(value.landing_url, '_blank')
                              } else if (value?.scope) {
                                window.open(`/landing/${value?.scope}`)
                              }
                            }}
                          >
                            了解详情
                          </Button>
                        ) : null}
                      </>
                    ) : (
                      <>
                        <Skeleton variant='rectangular' width={130} height={35} />
                        <Skeleton variant='rectangular' width={130} height={35} />
                      </>
                    )}
                  </ContentFooter>
                </Item>
              )
            })}
          </Stack>
        ) : (
          <Text
            size='xs'
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexDirection: 'column',
              color: grayText,
              margin: 'auto',
            }}
            weight='regular'
          >
            <ImageIcon src={empty} sx={{ width: '48px' }} />
            暂无产品
          </Text>
        )
      ) : (
        <Stack
          spacing={0}
          direction='column'
          alignItems='start'
          sx={{ width: '100%', textAlign: 'start', boxShadow: 'none' }}
        >
          <Skeleton variant='rectangular' width='80%' height={35} />
          <Skeleton variant='rectangular' width='80%' height={35} />
          <Skeleton variant='rectangular' width='80%' height={35} />
        </Stack>
      )}
    </InfoWrapper>
  )
}

export default Marketplace
